<template lang="pug">
#resident-group-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者グループ情報登録

    .box-body.register-body
      FormInput(
        key-name="グループ名"
        required
        name="groupname"
        v-model="name"
        type="text"
        placeholder="グループ名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmRegisterModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoResidentGroupList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="利用者グループ情報を登録しますか？" @ok="registerResidentGroup")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoResidentGroupList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";
import ResidentGroupsService, { ResidentGroupsGetModel, ResidentGroupsPostModel } from "@/commons/services/ResidentGroups";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";

/**
 * 利用者グループ登録画面コンポーネント
 */
@Component({
  components: {
    FormInput,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class ResidentGroupRegister extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** グループ名 */
  name: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * ### 登録確認ダイアログを表示する
   *
   * - バリデーションを実施する
   * - エラーでなければ確認ダイアログを表示する
   */
  public async showConfirmRegisterModal(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const validationResult: boolean = await this.$validator.validateAll();
    const groupNameValidationResult: boolean = await this.validateResidentGroupName(this.name);

    this.isBusy = false;

    if (validationResult && groupNameValidationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 利用者グループ名バリデーション
   */
  public async validateResidentGroupName(groupName: string): Promise<boolean> {
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    for (const group of groups) {
      if (group.name == groupName) {
        this.errorMessage = "既に使用されている利用者グループ名は登録できません";
        return false;
      }
    }

    return true;
  }

  /**
   * ### バリデーションチェック用の利用者グループ取得
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループを登録する
   *
   * - ローディングを表示
   * - 利用者グループサービスを使用して利用者グループを登録する
   * - 例外が無ければ成功ダイアログを表示する
   */
  public async registerResidentGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const residentGroup: ResidentGroupsPostModel = this.createRegisterParam();
    await this.residentGroupsService.post(userStore, residentGroup);

    // 利用者一覧を再読み込みさせるようstoreを更新
    let residentList: ResidentListState = needRefreshesStore.residentList;
    residentList.residentList = true;
    needRefreshesStore.setResidentList(residentList);
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * 利用者グループ登録パラメータ作成
   */
  public createRegisterParam(): ResidentGroupsPostModel {
    const residentGroup: ResidentGroupsPostModel = {
      name: this.$stringUtil.convertYen(this.name)
    };

    return residentGroup;
  }

  /**
   * ### 利用者グループ一覧画面に遷移する
   */
  public gotoResidentGroupList(): void {
    this.$router.push(PcUrl.ResidentGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#resident-group-register {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
